import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';
import styled from '@emotion/styled';
import SubNavigation from 'components/Navigation/SubNavigation';
import type { RouteConfig } from 'components/Navigation/SubNavigation/types';
import { useBreakpoint } from 'app/spacing';
import { MobileFallback } from 'pages/Common/MobileFallback';
import MarketplacePage from 'pages/Marketplace';
import { useBusinessAppPermissions } from 'pages/BusinessApps/permissions';
import { PreviewEndedToasts } from 'pages/BusinessApps/components/PreviewEndedToasts';
import { BusinessAppsList } from 'pages/BusinessApps/subpages/Apps';
import { APPS_TAB_PATHS } from './constants';
import { InstalledTab, useInstalledRows } from './subpages/Installed';

const StyledSubNavWrapper = styled.div`
  max-width: 600px;
  margin: 20px auto 0;
`;

export const AppsPage = () => {
  const { t } = useTranslation();
  const isMobile = useBreakpoint();
  const { canViewBusinessApps } = useBusinessAppPermissions();
  const { rows } = useInstalledRows();
  const installedHasErrors = rows.some(({ health }) => health === 'error');

  const routes = useMemo(() => {
    const tabs: Record<string, RouteConfig> = {
      marketplace: {
        path: APPS_TAB_PATHS.marketplace,
        label: t('Marketplace'),
        icon: 'store',
        exact: true,
      },
      installed: {
        path: APPS_TAB_PATHS.installed,
        label: t('Installed'),
        icon: 'grid-2',
        badge: installedHasErrors,
        exact: true,
      },
    };
    if (canViewBusinessApps) {
      tabs.develop = {
        path: APPS_TAB_PATHS.develop,
        label: t('Develop'),
        icon: 'code-light',
        exact: true,
      };
    }
    return tabs;
  }, [t, installedHasErrors, canViewBusinessApps]);

  return (
    <>
      <PreviewEndedToasts />
      <StyledSubNavWrapper>
        <SubNavigation routes={routes} id="apps-subnavigation" />
      </StyledSubNavWrapper>
      <Switch>
        <Route exact path={APPS_TAB_PATHS.marketplace}>
          <MarketplacePage />
        </Route>
        <Route exact path={APPS_TAB_PATHS.installed}>
          <MobileFallback isMobile={isMobile}>
            <InstalledTab />
          </MobileFallback>
        </Route>
        <Route exact path={APPS_TAB_PATHS.develop}>
          {canViewBusinessApps ? (
            <MobileFallback isMobile={isMobile}>
              <BusinessAppsList />
            </MobileFallback>
          ) : (
            <Redirect to={APPS_TAB_PATHS.installed} />
          )}
        </Route>
      </Switch>
    </>
  );
};
